import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/AuthProvider";
import { SEO } from "@/components/common/SEO";
import { Logo } from "@/components/common/Logo";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { AppShowcase } from "@/components/marketing/AppShowcase";
import { CheckCircle, Star, Users } from "lucide-react";
import { SocialProof } from "@/components/marketing/SocialProof";
import { FeatureHighlights } from "@/components/marketing/FeatureHighlights";
import { FreeForeverSection } from "@/components/features/FreeForeverSection";
import { MobileViewportMeta } from "@/components/mobile/MobileViewportMeta";
import { useIsMobile } from "@/hooks/use-mobile";

export default function Landing() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const isMobile = useIsMobile();

  const handleGetStarted = () => {
    navigate("/auth?mode=signup");
  };

  // Structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Hour Tracker",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": "Time tracking software for any workflow that generates professional invoices and helps freelancers get paid faster. Perfect for any business model.",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "142"
    }
  };

  return (
    <>
      <MobileViewportMeta />
      <div className="min-h-screen flex flex-col">
        <SEO 
          title="Time Tracking Software for Any Workflow | Hour Tracker"
          description="Time tracking software for any workflow that generates professional invoices and helps freelancers get paid faster. Perfect for any business model."
          keywords="time tracking software, workflow optimization, freelancer tool, invoice generator, billable hours, project management, productivity tracker, small business software, any workflow"
          ogImage="/og-image.png"
          structuredData={structuredData}
        />
        
        <header className="border-b sticky top-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm ios-safe-area-top">
          <div className="container flex h-16 items-center justify-between">
            <div className="flex items-center gap-6">
              <Logo size="medium" />
              {!isMobile && (
                <NavigationMenu>
                  <NavigationMenuList>
                    <NavigationMenuItem>
                      <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
                        <Link to="/learn-more">Features</Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                      <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
                        <Link to="/blog">Blog</Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                      <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
                        <Link to="/resources">Resources</Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>
              )}
            </div>
            <div className="flex gap-2">
              {session ? (
                <Button onClick={() => navigate("/dashboard")} className={isMobile ? "mobile-button px-4" : ""}>
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate("/auth")}
                    className={isMobile ? "mobile-touch-target px-4" : ""}
                  >
                    Log In
                  </Button>
                  <Button 
                    onClick={() => navigate("/auth?mode=signup")}
                    className={isMobile ? "mobile-touch-target px-4" : ""}
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1">
          <section className={`${isMobile ? 'py-12' : 'py-16 md:py-20'} bg-gradient-to-b from-background to-muted/20 mobile-section-padding`}>
            <div className="container flex flex-col items-center text-center max-w-3xl">
              <h1 className={`${isMobile ? 'text-3xl' : 'text-3xl md:text-4xl lg:text-6xl'} font-bold tracking-tight mb-6`}>
                Time Tracking Software for <span className="text-primary">Any Workflow</span>
              </h1>
              <p className={`${isMobile ? 'text-base' : 'text-lg md:text-xl'} text-muted-foreground mb-8`}>
                The simplest way for freelancers to track billable hours and generate professional invoices, no matter your workflow.
              </p>
              <div className={`${isMobile ? 'w-full' : 'flex flex-col sm:flex-row gap-4 w-full sm:w-auto'}`}>
                <Button 
                  size={isMobile ? "default" : "lg"} 
                  onClick={handleGetStarted} 
                  className={`${isMobile ? 'mobile-button animate-pulse' : 'w-full sm:w-auto animate-pulse'}`}
                >
                  Start Tracking Now
                </Button>
              </div>
              
              <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-center'} mt-8 gap-4 text-sm text-muted-foreground`}>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                  <span>Any Workflow</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                  <span>Secure Data</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                  <span>Unlimited Projects</span>
                </div>
              </div>
            </div>
          </section>

          {/* Social Proof Section */}
          <SocialProof />

          {/* App Showcase Section */}
          <AppShowcase />

          {/* Feature Highlights Section */}
          <FeatureHighlights />

          <section className={`${isMobile ? 'py-12' : 'py-20'} mobile-section-padding`}>
            <div className="container max-w-5xl">
              <div className="text-center mb-10">
                <h2 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold mb-4`}>Workflow-Optimized Time Tracking</h2>
                <p className={`${isMobile ? 'text-base' : 'text-lg'} text-muted-foreground max-w-2xl mx-auto`}>
                  Designed to fit seamlessly into any workflow, for independent professionals and small teams.
                </p>
              </div>
              <div className={`grid ${isMobile ? 'grid-cols-1 gap-6' : 'md:grid-cols-3 gap-8'}`}>
                <div className="glass-card p-6 rounded-lg hover-scale">
                  <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold mb-2`}>Flexible Time Tracking</h3>
                  <p className="text-muted-foreground">
                    Easily log your hours against different projects with our workflow-friendly interface.
                  </p>
                </div>
                <div className="glass-card p-6 rounded-lg hover-scale">
                  <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold mb-2`}>Project Management</h3>
                  <p className="text-muted-foreground">
                    Organize your work into projects that adapt to your workflow, each with their own hourly rates.
                  </p>
                </div>
                <div className="glass-card p-6 rounded-lg hover-scale">
                  <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold mb-2`}>Invoice Generation</h3>
                  <p className="text-muted-foreground">
                    Create professional invoices based on your tracked time with a single click, regardless of your workflow.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <FreeForeverSection />

          <section className={`${isMobile ? 'py-12' : 'py-16'} bg-muted/20 mobile-section-padding`}>
            <div className="container max-w-3xl text-center">
              <h2 className={`${isMobile ? 'text-xl' : 'text-2xl md:text-3xl'} font-bold mb-4`}>Join 5,000+ Professionals Using Our Workflow Software</h2>
              <p className="text-muted-foreground mb-8">
                Start tracking your time more efficiently and get paid faster, no matter your workflow.
              </p>
              <Button 
                size={isMobile ? "default" : "lg"} 
                onClick={handleGetStarted} 
                className={`${isMobile ? 'mobile-button' : 'px-8'}`}
              >
                Get Started Now
              </Button>
              <p className="text-sm text-muted-foreground mt-4">
                Set up in less than 2 minutes.
              </p>
            </div>
          </section>
        </main>

        <footer className={`border-t ${isMobile ? 'py-6' : 'py-8'} mobile-section-padding ios-safe-area-bottom`}>
          <div className="container text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} Hour Tracker. All rights reserved.</p>
            <div className={`flex ${isMobile ? 'flex-col gap-2 mt-4' : 'justify-center gap-4 mt-2'}`}>
              <Link to="/privacy-terms" className="hover:underline mobile-touch-target">
                Privacy Policy & Terms
              </Link>
              <Link to="/blog" className="hover:underline mobile-touch-target">
                Blog
              </Link>
              <Link to="/resources#faq" className="hover:underline mobile-touch-target">
                FAQ
              </Link>
              <Link to="/resources" className="hover:underline mobile-touch-target">
                Resources
              </Link>
              <Link to="/contact" className="hover:underline mobile-touch-target">
                Contact
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
